import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { UP } from '@/lib/motion'

const BEFORE_AFTER_IMAGES = [
  {
    before: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&q=80',
    after: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&q=80',
    label: 'Sofa Cleaning',
    amharic: 'ሶፋ ፅዳት'
  },
  {
    before: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
    after: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
    label: 'Living Room',
    amharic: 'የቤት ፅዳት'
  },
  {
    before: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=800&q=80',
    after: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=800&q=80',
    label: 'Kitchen',
    amharic: 'የማብሰያ ቤት ፅዳት'
  },
  {
    before: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=800&q=80',
    after: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=800&q=80',
    label: 'Mattress',
    amharic: 'የፍራሽ ፅዳት'
  }
]

function BeforeAfterSlider({
  before,
  after,
  label,
  amharic
}: {
  before: string
  after: string
  label: string
  amharic: string
}) {
  const [pct, setPct] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const getNewPct = (clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return 50
    return Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100))
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return
      setPct(getNewPct(e.clientX))
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return
      setPct(getNewPct(e.touches[0].clientX))
    }

    const handleEnd = () => setIsDragging(false)

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleEnd)
      window.addEventListener('touchmove', handleTouchMove)
      window.addEventListener('touchend', handleEnd)
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleEnd)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleEnd)
    }
  }, [isDragging])

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        borderRadius: 20,
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'grab',
        aspectRatio: '16/10'
      }}
      onMouseDown={(e) => {
        setIsDragging(true)
        setPct(getNewPct(e.clientX))
      }}
      onTouchStart={(e) => {
        setIsDragging(true)
        setPct(getNewPct(e.touches[0].clientX))
      }}
    >
      {/* After image (full) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${after})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Before image (clipped) */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${before})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          clipPath: `inset(0 ${100 - pct}% 0 0)`
        }}
      />

      {/* Labels */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          padding: '6px 12px',
          background: 'var(--red)',
          borderRadius: 8,
          fontFamily: 'DM Sans',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--white)',
          textTransform: 'uppercase'
        }}
      >
        BEFORE · ፊት
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          padding: '6px 12px',
          background: 'var(--lime)',
          borderRadius: 8,
          fontFamily: 'DM Sans',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--black)',
          textTransform: 'uppercase'
        }}
      >
        AFTER · ✓ በኋላ
      </motion.div>

      {/* Slider handle */}
      <motion.div
        data-cursor="drag"
        style={{
          position: 'absolute',
          left: `${pct}%`,
          top: 0,
          bottom: 0,
          width: 2,
          background: 'var(--lime)',
          x: '-50%',
          boxShadow: '0 0 20px rgba(232,255,0,0.5)',
          zIndex: 10
        }}
      >
        <motion.div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            x: '-50%',
            y: '-50%',
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'var(--lime)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--black)',
            fontSize: '1.1rem',
            fontWeight: 800,
            boxShadow: '0 0 30px rgba(232,255,0,0.6)'
          }}
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          whileHover={{ scale: 1.2 }}
        >
          ↔
        </motion.div>
      </motion.div>

      {/* Label at bottom */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
          fontFamily: 'DM Sans',
          fontSize: '1rem',
          fontWeight: 600,
          color: 'var(--white)'
        }}
      >
        {label} · {amharic}
      </div>
    </div>
  )
}

export function BA() {
  return (
    <section
      style={{
        background: 'var(--black)',
        padding: 'var(--pad-section) max(5vw, 24px)'
      }}
    >
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {/* Section header */}
        <motion.div {...UP} style={{ marginBottom: 48, textAlign: 'center' }}>
          <span
            style={{
              fontFamily: 'DM Sans',
              fontSize: '0.8rem',
              color: 'var(--lime)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase'
            }}
          >
            Real Results · እውነተኛ ውጤቶች
          </span>
          <h2
            style={{
              fontFamily: 'Bebas Neue',
              fontSize: 'var(--h2)',
              color: 'var(--white)',
              marginTop: 12,
              lineHeight: 1
            }}
          >
            Before & After
          </h2>
          <p
            style={{
              fontFamily: 'DM Sans',
              fontSize: '1rem',
              color: 'var(--white-60)',
              marginTop: 16,
              maxWidth: 500,
              margin: '16px auto 0'
            }}
          >
            Drag to see the transformation. Real results from real homes in Addis Ababa.
          </p>
        </motion.div>

        {/* Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 24
          }}
        >
          {BEFORE_AFTER_IMAGES.map((img) => (
            <BeforeAfterSlider key={img.label} {...img} />
          ))}
        </div>
      </div>
    </section>
  )
}
